// react
import { SyntheticEvent, useState } from 'react';

// types
import { StatsCounts, StatsEngagement, StatsEvidence, StatsFundFlow } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Button, Container, Grid, Paper } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { Tabs } from '../../../ui/Tabs/Tabs';

// tables
import { TableBasicProjectsEngagement } from '../../../tables/basic/TableBasicProjectsEngagement/TableBasicProjectsEngagement';
import { TableBasicProjectsCounts } from '../../../tables/basic/TableBasicProjectsCounts/TableBasicProjectsCounts';
import { TableBasicProjectsFundFlow } from '../../../tables/basic/TableBasicProjectsFundFlow/TableBasicProjectsFundFlow';
import { TableBasicProjectsEvidence } from '../../../tables/basic/TableBasicProjectsEvidence/TableBasicProjectsEvidence';

// dialogs
import { DialogProjectReport } from '../../../dialogs/DialogProjectReport/DialogProjectReport';

// components
import { StatsTab } from './StatsTab/StatsTab';

export const Adoption = () => {
	// hooks
	const { t } = useTranslation();

	// active tab (engagement | counts | activity | evidence)
	const [tab, setTab] = useState('engagement');

	// report dialog open state driven by ?dialog=statsReport
	const [searchParams, setSearchParams] = useSearchParams();

	// handlers
	const handleChangeTab = (_: SyntheticEvent, newValue: string) => {
		setTab(newValue);
	};

	// tabs (one per financial report workbook sheet)
	const tabs = [
		{ label: t('engagement'), value: 'engagement' },
		{ label: t('countStatistics'), value: 'counts' },
		{ label: t('inflowOutflow'), value: 'activity' },
		{ label: t('financialEvidence'), value: 'evidence' },
	];

	const handleExportFinancial = () => {
		searchParams.set('dialog', 'statsReport');
		setSearchParams(searchParams);
	};

	const handleCloseFinancialDialog = () => {
		searchParams.delete('dialog');
		setSearchParams(searchParams);
	};

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Grid alignItems="center" container spacing={2}>
						<Grid item xs={12} md={true}>
							<Tabs hasBorder={false} onChange={handleChangeTab} tabs={tabs} value={tab} />
						</Grid>
						<Grid item xs={12} md="auto">
							<Button onClick={handleExportFinancial} variant="contained">
								{t('emailReport')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<TabContext value={tab}>
						<TabPanel sx={{ p: 0 }} value="engagement">
							<StatsTab<StatsEngagement>
								defaultOrderBy="avgPerMonth"
								isActive={tab === 'engagement'}
								type="engagement"
								render={({ data, isLoading, onSort, TableFooterProps }) => (
									<Paper>
										<TableBasicProjectsEngagement
											BoxFooterProps={{ sx: { border: 'none' } }}
											color="grey"
											data={data}
											isLoading={isLoading}
											onSort={onSort}
											sxBox={{ borderRadius: 2 }}
											TableFooterProps={TableFooterProps}
										/>
									</Paper>
								)}
							/>
						</TabPanel>

						<TabPanel sx={{ p: 0 }} value="counts">
							<StatsTab<StatsCounts>
								isActive={tab === 'counts'}
								type="counts"
								render={({ data, isLoading, onSort, TableFooterProps }) => (
									<Paper>
										<TableBasicProjectsCounts
											BoxFooterProps={{ sx: { border: 'none' } }}
											color="grey"
											data={data}
											isLoading={isLoading}
											onSort={onSort}
											sxBox={{ borderRadius: 2 }}
											TableFooterProps={TableFooterProps}
										/>
									</Paper>
								)}
							/>
						</TabPanel>

						<TabPanel sx={{ p: 0 }} value="activity">
							<StatsTab<StatsFundFlow>
								isActive={tab === 'activity'}
								type="activity"
								render={({ data, isLoading, onSort, TableFooterProps }) => (
									<Paper>
										<TableBasicProjectsFundFlow
											BoxFooterProps={{ sx: { border: 'none' } }}
											color="grey"
											data={data}
											isLoading={isLoading}
											onSort={onSort}
											sxBox={{ borderRadius: 2 }}
											TableFooterProps={TableFooterProps}
										/>
									</Paper>
								)}
							/>
						</TabPanel>

						<TabPanel sx={{ p: 0 }} value="evidence">
							<StatsTab<StatsEvidence>
								isActive={tab === 'evidence'}
								type="evidence"
								render={({ data, isLoading, onSort, TableFooterProps }) => (
									<Paper>
										<TableBasicProjectsEvidence
											BoxFooterProps={{ sx: { border: 'none' } }}
											color="grey"
											data={data}
											isLoading={isLoading}
											onSort={onSort}
											sxBox={{ borderRadius: 2 }}
											TableFooterProps={TableFooterProps}
										/>
									</Paper>
								)}
							/>
						</TabPanel>
					</TabContext>
				</Grid>
			</Grid>

			<DialogProjectReport
				isOpen={searchParams.get('dialog') === 'statsReport'}
				onClose={handleCloseFinancialDialog}
			/>
		</Container>
	);
};
